/**
 * useLabelPrint Hook
 * ──────────────────
 * Reusable hook for printing labels via Bluetooth thermal printer.
 * Used by all three roles: Sender, Transit, Receiver.
 *
 * Usage:
 *   const { printShipmentLabel, isPrinting, printerConnected } = useLabelPrint();
 *   await printShipmentLabel({ tracking_no: 'xxx', weight: 12.5 });
 */

import { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useBluetoothStore } from '../store/bluetooth.store';
import { useLabelTemplateStore, LabelSize, LabelTemplate } from '../store/label-template.store';
import { printLabel, buildPrintData, LabelPrintData, PrintProtocol } from '../services/label-print.service';
import { usePrintScenarioStore, PrintScenario } from '../store/print-scenario.store';

interface PrintShipmentParams {
    tracking_no?: string;
    weight?: number;
    length?: number;
    width?: number;
    height?: number;
    batch_no?: string;
    shipper_name?: string;
    receiver_name?: string;
    receiver_phone?: string;
    receiver_address?: string;
    sender_name?: string;
    sender_phone?: string;
    sender_address?: string;
    pieces?: number;
    remark?: string;
}

interface UseLabelPrintOptions {
    /** Override label size (default: use printer's configured size) */
    size?: LabelSize;
    /** Print protocol */
    protocol?: PrintProtocol;
    /** Number of copies */
    copies?: number;
    /** If true, silently skip if no printer connected (no error thrown) */
    silent?: boolean;
    /** Feature identifier (e.g. 'cargo_create') to use a specific template */
    feature?: string;
    /** Specific scenario ID to use (overrides everything) */
    scenarioId?: string;
}

interface UseLabelPrintReturn {
    /** Print a label with shipment data */
    printShipmentLabel: (params: PrintShipmentParams) => Promise<boolean>;
    /** Is currently printing */
    isPrinting: boolean;
    /** Is printer connected via Bluetooth */
    printerConnected: boolean;
    /** Printer device name */
    printerName: string | null;
}

export function useLabelPrint(options: UseLabelPrintOptions = {}): UseLabelPrintReturn {
    const { copies = 1, silent = false, feature } = options;
    const [isPrinting, setIsPrinting] = useState(false);

    const printerDevice = useBluetoothStore(s => s.printerDevice);
    const printSize = useBluetoothStore(s => s.printSize);
    const printProtocolStore = useBluetoothStore(s => s.printProtocol);
    const printDensityStore = useBluetoothStore(s => s.printDensity);
    const printGapStore = useBluetoothStore(s => s.printGap);
    const printAutoDetectGapStore = useBluetoothStore(s => s.printAutoDetectGap);
    const { getDefaultTemplate, templates, initDefaults, featureTemplates } = useLabelTemplateStore();
    const { getActiveScenario, scenarios, initDefaults: initScenarioDefaults } = usePrintScenarioStore();

    const printerConnected = !!printerDevice?.connected;
    const printerName = printerDevice?.name || null;

    const printShipmentLabel = useCallback(async (params: PrintShipmentParams): Promise<boolean> => {
        // Check printer
        if (!printerConnected) {
            if (silent) {
                console.log('[LabelPrint] No printer connected, skipping print');
                return false;
            }
            toast.error('蓝牙打印机未连接\n请先在设置→蓝牙中连接打印机', { duration: 3000, icon: '🔌' });
            return false;
        }

        setIsPrinting(true);

        try {
            // Ensure defaults exist
            initDefaults();
            initScenarioDefaults();

            let template: LabelTemplate | undefined;
            let activeScenario: PrintScenario | null = null;

            // 1. Resolve Scenario
            if (options.scenarioId) {
                activeScenario = scenarios.find(s => s.id === options.scenarioId) || null;
            } else if (feature) {
                // If feature-specific scenario selection is implemented, use it.
                // For now, use the globally active scenario from store.
                activeScenario = getActiveScenario();
            }

            // 2. Resolve Template
            if (feature) {
                const assignedId = featureTemplates[feature];
                if (assignedId) {
                    template = templates.find(t => t.id === assignedId);
                }
            }

            if (!template && activeScenario && activeScenario.templateId) {
                template = templates.find(t => t.id === activeScenario.templateId);
            }

            // 3. Size and Protocol logic
            const sizeMap: Record<string, LabelSize> = {
                '76x130': '76x130',
                '100x150': '100x150',
                '100x170': '100x170',
                '50x30': '50x30',
                '100x100': '100x100',
                '100x130': '100x130',
                '40x30': '40x30',
            };

            const labelSize: LabelSize = options.size || activeScenario?.printSize || sizeMap[printSize] || '76x130';

            // 4. Final Fallback for template
            if (!template) {
                template = getDefaultTemplate(labelSize);
            }
            if (!template) {
                template = templates.find(t => t.size === labelSize);
            }
            if (!template) {
                template = templates[0];
            }
            if (!template) {
                toast.error('没有可用的标签模板，请先在模板管理中创建模板');
                return false;
            }

            // Build print data
            const data: LabelPrintData = buildPrintData(params);

            // Print!
            const effectiveProtocol = options.protocol || activeScenario?.protocol || printProtocolStore || 'auto';
            const effectiveCopies = options.copies || activeScenario?.copies || copies;

            await printLabel(template, data, {
                protocol: effectiveProtocol,
                copies: effectiveCopies,
                density: printDensityStore,
                gap: printGapStore,
                autoDetectGap: printAutoDetectGapStore
            });

            toast.success('标签打印成功', { icon: '🖨️', duration: 2000 });
            return true;
        } catch (err: any) {
            console.error('[LabelPrint] Print error:', err);
            toast.error(`打印失败: ${err.message || '未知错误'}`, { duration: 3000 });
            return false;
        } finally {
            setIsPrinting(false);
        }
    }, [
        printerConnected,
        printSize,
        printProtocolStore,
        printDensityStore,
        printGapStore,
        printAutoDetectGapStore,
        options.size,
        options.protocol,
        options.copies,
        options.scenarioId,
        copies,
        silent,
        feature,
        featureTemplates,
        getDefaultTemplate,
        templates,
        scenarios,
        getActiveScenario,
        initDefaults,
        initScenarioDefaults
    ]);

    return {
        printShipmentLabel,
        isPrinting,
        printerConnected,
        printerName,
    };
}
